import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  TextField,
  ThemeProvider,
  createTheme,
  CircularProgress,
  Divider,
  Alert,
  Tooltip,
  Zoom,
  Fade,
  Checkbox,
  FormControlLabel,
  Stack
} from '@mui/material';


const theme = createTheme({
  palette: {
    primary: {
      main: '#4361ee',
    },
    secondary: {
      main: '#4caf50',
    },
    background: {
      default: '#1e2d58',
      paper: '#ffffff',
    },
    analysis: {
      red: '#ffcdd2',
      yellow: '#fff9c4',
      green: '#c8e6c9',
    },
    woo: {
      purple: '#9c27b0',
      blue: '#3f51b5',
      teal: '#009688',
      gold: '#ffc107',
    }
  },
  typography: {
    h2: {
      fontWeight: 700,
      color: '#ffffff',
      fontSize: '2.5rem',
    },
    subtitle1: {
      color: '#ffffff',
      fontSize: '1.1rem',
      marginBottom: '2rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      marginBottom: '1rem',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 50,
          padding: '12px 40px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1.25rem',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'white',
          color: 'rgba(0, 0, 0, 0.87)',
          maxWidth: 300,
          fontSize: '0.9rem',
          border: '1px solid #dadde9',
          boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
          padding: '16px',
          borderRadius: '12px',
        },
        arrow: {
          color: 'white'
        }
      }
    }
  },
});


// Magic Loading Animation component
const MagicLoadingAnimation = ({ progress }) => {
  const [animationPhase, setAnimationPhase] = useState(0);
  const [loadingMessages, setLoadingMessages] = useState([
    // "Summoning truth crystals...",
    // "Consulting the ancient scrolls...",
    // "Casting detection spells...",
    // "Aligning magical nodes...",
    // "Divining factual essence..."
    "",
    "",
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationPhase(prev => (prev + 1) % loadingMessages.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [loadingMessages.length]);

  return (
    <Box sx={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      p: 4,
      position: 'relative'
    }}>
      {/* Magical circle */}
      <Box sx={{
        width: 200,
        height: 200,
        borderRadius: '50%',
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        mb: 3
      }}>
        {/* Outer spinning ring */}
        <Box sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          border: '3px solid',
          borderColor: theme.palette.woo.gold,
          animation: 'spin 10s linear infinite',
          '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' }
          }
        }}>
          {/* Runes/symbols along the ring */}
          {[...Array(8)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                width: 15,
                height: 15,
                bgcolor: theme.palette.woo.gold,
                top: '50%',
                left: '50%',
                transform: `rotate(${i * 45}deg) translateX(100px) translateY(-50%)`,
                borderRadius: '50%'
              }}
            />
          ))}
        </Box>

        {/* Middle ring (counter-rotating) */}
        <Box sx={{
          position: 'absolute',
          width: '70%',
          height: '70%',
          borderRadius: '50%',
          border: '2px dashed',
          borderColor: theme.palette.woo.purple,
          animation: 'spin-reverse 7s linear infinite',
          '@keyframes spin-reverse': {
            '0%': { transform: 'rotate(360deg)' },
            '100%': { transform: 'rotate(0deg)' }
          }
        }} />

        {/* Inner progress circle */}
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant="determinate"
            value={progress}
            size={80}
            thickness={8}
            sx={{
              color: theme.palette.woo.teal,
              filter: 'drop-shadow(0 0 6px rgba(0, 150, 136, 0.7))'
            }}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h6" component="div" sx={{ color: theme.palette.woo.gold, fontWeight: 'bold' }}>
              {progress}%
            </Typography>
          </Box>
        </Box>

        {/* Floating magic particles */}
        {[...Array(12)].map((_, i) => (
          <Box
            key={`particle-${i}`}
            sx={{
              position: 'absolute',
              width: 5,
              height: 5,
              bgcolor: ['#9c27b0', '#3f51b5', '#009688', '#ffc107'][i % 4],
              borderRadius: '50%',
              filter: 'blur(1px)',
              opacity: 0.7,
              animation: `float-${i} ${3 + i % 5}s infinite ease-in-out`,
              [`@keyframes float-${i}`]: {
                '0%, 100%': {
                  transform: `translate(${-50 + Math.random() * 100}px, ${-50 + Math.random() * 100}px)`,
                  opacity: 0.3
                },
                '50%': {
                  transform: `translate(${-50 + Math.random() * 100}px, ${-50 + Math.random() * 100}px)`,
                  opacity: 0.9
                }
              }
            }}
          />
        ))}
      </Box>

      {/* Loading message */}
      <Typography
        variant="h6"
        sx={{
          color: theme.palette.woo.gold,
          textAlign: 'center',
          height: 28,
          fontStyle: 'italic',
          animation: 'fade 2s infinite ease-in-out',
          '@keyframes fade': {
            '0%, 100%': { opacity: 0.5 },
            '50%': { opacity: 1 }
          }
        }}
      >
        {loadingMessages[animationPhase]}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: '#ffffff',
          mt: 2,
          opacity: 0.7,
          textAlign: 'center'
        }}
      >
        {/* Our truth wizards are hard at work analyzing your content... */}
        The content is being analyzed by our Algorithm
      </Typography>
    </Box>
  );
};

export default MagicLoadingAnimation;